import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';

import { useConfig } from '@entities/config/api';
import { ROUTES } from '@shared/config/routes';
import { Skeleton } from '@shared/ui';

import { LegalArticle } from './LegalArticle';

/**
 * Erklärung zur Barrierefreiheit — BFSG (действует с 28.06.2025).
 *
 * Страница показывается **только** при `BFSG_APPLICABLE=true`. Причина не в
 * экономии: BFSG знает исключение для Kleinstunternehmen (менее 10 работников
 * **и** не более 2 млн € оборота), и публиковать заявление о соответствии, не
 * будучи обязанным и не проведя аудит, — то же самое ложное утверждение, что и
 * «мы проверили автомобиль». Пока ответ оператора не получен, флаг выключен, а
 * ссылка в футере не рисуется.
 *
 * Текст называет опорную норму (EN 301 549 / WCAG 2.1 AA) и **канал обратной
 * связи**: возможность сообщить о барьере — часть требования, а не вежливость.
 */
export function AccessibilityPage() {
  const { t } = useTranslation();
  const { data, isPending } = useConfig();

  if (isPending || !data) {
    return (
      <LegalArticle title="Erklärung zur Barrierefreiheit">
        <Skeleton height="14rem" radius="var(--radius-card)" />
      </LegalArticle>
    );
  }

  if (!data.legal.accessibility_statement_enabled) {
    // Заявления не существует — значит и адреса не существует. Пустая страница
    // с текстом «раздел недоступен» была бы хуже: она выглядит как ошибка.
    return <Navigate to={ROUTES.home} replace />;
  }

  const operator = data.legal.operator;

  return (
    <LegalArticle
      title="Erklärung zur Barrierefreiheit"
      version={data.legal.document_versions.accessibility}
      lead={<p>{t('legal.accessibility.lead')}</p>}
    >
      <h2>Geltungsbereich</h2>
      <p>
        Diese Erklärung gilt für die Website und die Weboberfläche von CarViQ.DE unter{' '}
        {operator.website} einschließlich des Bestellvorgangs.
      </p>

      <h2>Angewandte Anforderungen</h2>
      <p>
        CarViQ.DE orientiert sich an der harmonisierten europäischen Norm EN 301 549 und damit an
        den Erfolgskriterien der Web Content Accessibility Guidelines (WCAG) 2.1 auf Stufe AA.
        Umgesetzt sind insbesondere: vollständige Bedienbarkeit per Tastatur mit sichtbarem Fokus,
        semantische Überschriftenstruktur und Sprungmarken zum Inhalt, Textalternativen für
        informationstragende Bilder, Kontrastverhältnisse nach WCAG 2.1 AA sowie der Grundsatz, dass
        Farbe nie der einzige Bedeutungsträger ist — jede farbliche Auszeichnung wird zusätzlich
        durch Text und Symbol getragen. Formulare besitzen dauerhaft sichtbare Beschriftungen;
        Fehler werden als Text ausgegeben und nicht allein durch eine farbige Umrandung angezeigt.
      </p>

      <h2>Bekannte Einschränkungen</h2>
      <p>
        Inhalte, die von Autohäusern stammen — etwa Beschreibungstexte und Fotos aus Angeboten —
        werden unverändert wiedergegeben. Auf ihre Barrierefreiheit, insbesondere auf
        Textalternativen zu Fotos, hat CarViQ.DE keinen Einfluss.
      </p>

      <h2>Rückmeldung und Kontakt</h2>
      <p>
        Sind Ihnen Barrieren aufgefallen oder benötigen Sie Inhalte in einer zugänglicheren Form,
        schreiben Sie an <a href={`mailto:${operator.email}`}>{operator.email}</a>
        {operator.phone ? ` oder rufen Sie an: ${operator.phone}` : ''}. Wir antworten unverzüglich
        und nennen, sofern eine Barriere nicht sofort behoben werden kann, einen Weg, den Inhalt
        anders zu erhalten.
      </p>

      <h2>Durchsetzungsverfahren</h2>
      <p>
        Hilft unsere Antwort nicht weiter, können Sie sich an die Marktüberwachungsstelle der Länder
        für die Barrierefreiheit von Produkten und Dienstleistungen (MLBF) wenden.
      </p>
    </LegalArticle>
  );
}
